import { apiClient } from './api'
import {
  NotificationTemplate,
  CreateNotificationTemplateRequest,
  TemplatePreviewRequest,
  TemplatePreviewResponse,
  TemplateMetadata
} from '@shared/types/notificationTemplate'

export const notificationTemplateService = {
  /**
   * Get all notification templates
   * Backend returns: { success: true, data: templates[] }
   * apiClient is the wrapped api object that returns response.data
   */
  async getAllTemplates(type: string = 'EMAIL'): Promise<NotificationTemplate[]> {
    const response = await apiClient.get<any>(`/notification-templates?type=${type}`)
    return response.data
  },

  /**
   * Get template versions by name
   * Backend returns: { success: true, data: templates[] }
   */
  async getTemplateVersions(name: string, type: string = 'EMAIL'): Promise<NotificationTemplate[]> {
    const response = await apiClient.get<any>(`/notification-templates/name/${name}?type=${type}`)
    return response.data
  },

  /**
   * Get active template by name
   * Backend returns: { success: true, data: template }
   * apiClient is the wrapped api object that returns response.data
   */
  async getActiveTemplate(name: string, type: string = 'EMAIL'): Promise<NotificationTemplate> {
    const response = await apiClient.get<any>(`/notification-templates/name/${name}/active?type=${type}`)
    return response.data
  },

  /**
   * Get template by ID
   * Backend returns: { success: true, data: template }
   * apiClient is the wrapped api object that returns response.data
   */
  async getTemplateById(id: string): Promise<NotificationTemplate> {
    const response = await apiClient.get<any>(`/notification-templates/${id}`)
    return response.data
  },

  /**
   * Create new template version
   * Backend returns: { success: true, data: template, message: string }
   * apiClient is the wrapped api object that returns response.data
   */
  async createTemplate(data: CreateNotificationTemplateRequest): Promise<NotificationTemplate> {
    const response = await apiClient.post<any>('/notification-templates', data)
    return response.data
  },

  /**
   * Activate a template version
   * Backend returns: { success: true, data: template, message: string }
   * apiClient is the wrapped api object that returns response.data
   */
  async activateTemplate(id: string): Promise<NotificationTemplate> {
    const response = await apiClient.put<any>(`/notification-templates/${id}/activate`)
    return response.data
  },

  /**
   * Preview template with sample variables
   * Backend returns: { success: true, data: { subject, bodyHtml, bodyText } }
   * apiClient is the wrapped api object that returns response.data
   */
  async previewTemplate(data: TemplatePreviewRequest): Promise<TemplatePreviewResponse> {
    const response = await apiClient.post<any>('/notification-templates/preview', data)
    return response.data
  },

  /**
   * Delete a template version
   * Backend returns: { success: true, message: string }
   */
  async deleteTemplate(id: string): Promise<void> {
    await apiClient.delete(`/notification-templates/${id}`)
  },

  /**
   * Get template metadata (available variables for each template type)
   * Backend returns: { success: true, data: Record<string, TemplateMetadata> }
   * apiClient is the wrapped api object that returns response.data
   */
  async getTemplateMetadata(): Promise<Record<string, TemplateMetadata>> {
    const response = await apiClient.get<any>('/notification-templates/metadata')
    return response.data
  }
}
